import Cookies from 'js-cookie';
import { PlanItem } from 'lib/plan-helpers';
import { GTM_DATALAYER, TrackingAction } from './constants/actions';

export const TRACKING_EVENT_NAME = 'conv-evt-trk';

const completeEventSourcesName = 'complete_event_sources';
const categorySelector = '[data-conversions-category]:not([data-conversions-category=""])';
const categoryKey = 'conversionsCategory';
const referringCategoryKey = 'referringConversionsCategory';

const localeMap = {
  en: 'English',
  es: 'Spanish',
  pt: 'Portuguese',
  de: 'German',
  fr: 'French',
  nl: 'Dutch',
  sv: 'Swedish',
  it: 'Italian',
};

// #region Types
/* Typescript Types */
declare global {
  interface Window {
      dataLayer:any;
  }
}

type PageDimensions = {
  'page_title'?:string
  'page_location'?:string
  'page_path'?:string
  'send_to'?:string
}

type UserDimensions = {
  'user_status_start'?:string
  'user_status_updated'?:string
  'user_plan'?:string
  'user_survey'?:string
  'free_download_count'?:string
  'purchase_count'?:string
  'ltv'?:string
  'split_test'?:string
  'test_block'?:string
  'feature_flags'?:string
}

type EventDimensions = {
  content_type?:string // eslint-disable-line camelcase
  error_trigger?:string // eslint-disable-line camelcase
}

type Dimensions = &
  PageDimensions &
  UserDimensions &
  EventDimensions

export type TrackingData = Dimensions & {
  action:TrackingAction|string
  category?:string
  coupon?:string
  currency?:string
  dataLayer?:{ [key: string]: any }
  dataLayerCompleteAttrs?:string[]
  dimensions?:Dimensions
  items?:PlanItem[]
  label?:string
  'non_interaction'?:boolean
  tax?:number
  'transaction_id'?:string
  value?:number|string
  valueAttribute?:string
  valueElementSelector?:string
}

export type TrackingEvent = {
  element?:HTMLElement
  data:TrackingData
}

/* End Typescript Types */
// #endregion

export function sendTrackingEvent({ element = null, data }:TrackingEvent) {
  const elem = element || document.body;

  const event = new CustomEvent(TRACKING_EVENT_NAME, {
    bubbles: true,
    detail: { ...data },
  });

  elem.dispatchEvent(event);
}

export function findCategoryByElement(element:HTMLElement) {
  const node = element.closest(categorySelector) as HTMLElement;
  return node ? node.dataset[categoryKey] : 'Unknown';
}

export function findReferringCategory(element:HTMLElement) {
  const node = element.closest(categorySelector) as HTMLElement;
  return (node && node.dataset[referringCategoryKey]) ? node.dataset[referringCategoryKey] : 'Unknown';
}

function getCookieDomain():string {
  const cookieDomainArray = window.location.hostname.split('.');
  return `${cookieDomainArray[1]}.${cookieDomainArray[2]}`;
}

export function getCompleteEventSources():TrackingData[] {
  return Cookies.getJSON(completeEventSourcesName);
}

export function deleteCompleteEventSources() {
  Cookies.remove(completeEventSourcesName, { domain: getCookieDomain() });
}

function completeEventTracked({ sources, action }: { sources:TrackingData[], action:TrackingAction|string }):boolean {
  if (!sources) return false;

  return sources.some((event:TrackingData) => event.action === action);
}

export function trackCompleteEvent({
  action,
  category,
  label = '',
  value = null,
  valueElementSelector = '',
  valueAttribute = '',
  dimensions = {},
  dataLayer,
  dataLayerCompleteAttrs,
}:TrackingData) {
  const sources = getCompleteEventSources();

  if (completeEventTracked({ sources, action })) return;

  let event:TrackingData = {
    action,
    category,
  };

  if (label) {
    event.label = label;
  }

  if (value) {
    event.value = value;
  }

  if (valueElementSelector) {
    event.valueElementSelector = valueElementSelector;
  }

  if (valueAttribute) {
    event.valueAttribute = valueAttribute;
  }

  if (dimensions) {
    event = { ...event, ...dimensions };
  }

  if (dataLayer) {
    event.dataLayer = dataLayer;
  }

  if (dataLayerCompleteAttrs) {
    event.dataLayerCompleteAttrs = dataLayerCompleteAttrs;
  }

  let updatedSources = [];

  if (sources && Array.isArray(sources)) {
    updatedSources = sources;
  }

  updatedSources.push(event);

  Cookies.set(completeEventSourcesName, updatedSources, { domain: getCookieDomain() });
}

export function processLocale(locale:string) {
  const lang = (localeMap[locale]) ? localeMap[locale] : 'Unknown';
  return `Language-${lang}`;
}

export function getAnalyticsDimensions() {
  const metaTag = document.querySelector('meta[name="analytics_data"]');
  return JSON.parse(metaTag.getAttribute('content'));
}

export function dataLayerPush(extraData:any = {}) {
  const eventData = {
    event: GTM_DATALAYER,
    ...getAnalyticsDimensions(),
    ...extraData,
  };

  delete eventData.snowplow_collector;
  delete eventData.snowplow_app_id;

  window.dataLayer.push(eventData);
}

export function pushDataLayerProperties(props:any = {}) {
  window.dataLayer.push(props);
}

export function getPageviewGuid() {
  return document.body.getAttribute('data-pageview-guid');
}
